import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Send, Pencil, Sparkles, AlertTriangle } from "lucide-react";
import MultipackPreview from "@/components/MultipackPreview";
import CoopItemsEditor from "@/components/CoopItemsEditor";
import { isCoopType, coopTotals } from "@/lib/coop";

const EMPTY = {
  title: "", description: "", retailer: "", product_name: "", category: "General",
  image_url: "", deal_type: "Buy One Get One Free", original_price: "", deal_price: "", terms: "", source: "", deal_url: "",
  line_items: [],
};

const fromDeal = (d) => ({
  title: d.title || "", description: d.description || "", retailer: d.retailer || "",
  product_name: d.product_name || "", category: d.category || "General",
  image_url: d.image_url || "", deal_type: d.deal_type || "Buy One Get One Free",
  original_price: d.original_price ?? "", deal_price: d.deal_price ?? "",
  terms: d.terms || "", source: d.source || "", deal_url: d.deal_url || "",
  line_items: Array.isArray(d.line_items) ? d.line_items : [],
});

export default function SubmitDealDialog({ onSubmitted, triggerClassName, editDeal = null, trigger = null }) {
  const isEdit = !!editDeal;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [flyerWarnings, setFlyerWarnings] = useState([]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadFlyer = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/deals/extract-flyer", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      setForm((prev) => ({
        ...prev,
        title: data.title || prev.title,
        retailer: data.retailer || prev.retailer,
        product_name: data.product_name || prev.product_name,
        category: data.category || prev.category,
        deal_type: data.deal_type || prev.deal_type,
        original_price: data.original_price || prev.original_price,
        deal_price: data.deal_price || prev.deal_price,
        description: data.description || prev.description,
        expires_at: data.expiry_date || prev.expires_at,
        image_url: data.image_path ? BACKEND + data.image_path : prev.image_url,
      }));
      toast.success("Flyer read — fields pre-filled. Please review & edit before submitting.");
      setFlyerWarnings(data.warnings || []);
      if ((data.warnings || []).length) {
        toast.warning(`Double-check: ${data.warnings.map((w) => w.label).join(", ")}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't read the flyer. Fill the fields manually.");
    } finally {
      setExtracting(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    if (open) setForm(isEdit ? fromDeal(editDeal) : EMPTY);
    if (open) setFlyerWarnings([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOutcome = (data) => {
    if (data.outcome === "auto_posted") toast.success("Verified & posted live! 🎉 Your deal is on the board.");
    else if (data.outcome === "rejected") toast.error(`Rejected by auto-moderation: ${data.reject_reason || "inappropriate content"}`);
    else toast.success(isEdit ? "Saved! An admin will review your changes." : "Submitted! An admin will review it shortly.");
  };

  const submit = async () => {
    setBusy(true);
    try {
      const coop = isCoopType(form.deal_type);
      const items = coop ? (form.line_items || []).filter((it) => String(it.name || "").trim()) : null;
      if (coop && !items.length) {
        toast.error("Add at least one product to the co-op basket");
        setBusy(false);
        return;
      }
      const body = { ...form, original_price: parseFloat(form.original_price || 0), deal_price: parseFloat(form.deal_price || 0), line_items: items };
      const { data } = isEdit
        ? await api.put(`/deals/${editDeal.deal_id}`, body)
        : await api.post("/deals/submit", body);
      handleOutcome(data);
      setOpen(false);
      onSubmitted && onSubmitted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="submit-deal-btn" className={`rounded-full hard-shadow-sm hover:-translate-y-0.5 transition-transform duration-200 ${triggerClassName || ""}`}>
            <Plus className="mr-1 h-4 w-4" /> Submit a deal
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit your deal" : "Submit a deal you spotted"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Your changes are re-checked automatically. If the link is from a trusted retailer and passes moderation, it stays live; otherwise it returns to review."
              : "Paste the deal link. If it's from a trusted retailer and passes our automatic content check, it goes live instantly — otherwise an admin reviews it."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3">
            <input type="file" accept="image/jpeg,image/png,image/webp" id="submit-flyer-input"
              data-testid="submit-flyer-input" onChange={uploadFlyer} className="hidden" />
            <Button type="button" variant="outline" size="sm" disabled={extracting}
              data-testid="submit-flyer-btn"
              onClick={() => document.getElementById("submit-flyer-input").click()}
              className="w-full rounded-full border-primary/30">
              <Sparkles className="mr-1.5 h-4 w-4" />
              {extracting ? "Reading flyer…" : "Upload a flyer photo — AI auto-fills the fields"}
            </Button>
            <p className="mt-1.5 text-center text-[11px] text-muted-foreground">
              Snap or upload the deal flyer and we'll pre-fill prices & details for you to review.
            </p>
          </div>
          {flyerWarnings.length > 0 && (
            <div data-testid="flyer-warnings" className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800">
              <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Please double-check these fields</div>
              <p className="mt-0.5">The AI wasn't sure about: <b>{flyerWarnings.map((w) => w.label).join(", ")}</b>.</p>
            </div>
          )}
          <div>
            <Label>Deal URL *</Label>
            <Input data-testid="submit-url-input" value={form.deal_url} onChange={set("deal_url")} placeholder="https://www.walmart.ca/…" />
          </div>
          <div><Label>Title *</Label><Input data-testid="submit-title-input" value={form.title} onChange={set("title")} placeholder="BOGO Free — Coffee Beans" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Retailer *</Label><Input data-testid="submit-retailer-input" value={form.retailer} onChange={set("retailer")} placeholder="Store name" /></div>
            <div><Label>Category</Label><Input value={form.category} onChange={set("category")} /></div>
          </div>
          <div><Label>Product</Label><Input value={form.product_name} onChange={set("product_name")} /></div>
          <div><Label>Deal type</Label>
            <select data-testid="submit-deal-type" value={form.deal_type} onChange={set("deal_type")}
              className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option>Buy One Get One Free</option>
              <option>Buy One, Get One at a Discount</option>
              <option>Bulk Buying Co-op</option>
            </select>
          </div>
          {isCoopType(form.deal_type) ? (
            <>
              <CoopItemsEditor
                items={form.line_items || []}
                onChange={(items) => setForm((f) => ({ ...f, line_items: items }))}
                idPrefix="submit-coop"
              />
              {coopTotals(form.line_items || []).priceTotal > 0 && (
                <MultipackPreview dealType={form.deal_type}
                  dealPrice={coopTotals(form.line_items || []).shareTotal}
                  originalPrice={coopTotals(form.line_items || []).priceTotal}
                  province={form.province || undefined} />
              )}
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Full price ($)</Label><Input data-testid="submit-price-input" type="number" value={form.original_price} onChange={set("original_price")} /></div>
              <div><Label>2nd item price ($)</Label><Input type="number" value={form.deal_price} onChange={set("deal_price")} placeholder="0 for free" /></div>
            </div>
          )}
          <div><Label>Image URL</Label><Input value={form.image_url} onChange={set("image_url")} placeholder="https://…" /></div>
          <div><Label>Where you found it</Label><Input value={form.source} onChange={set("source")} placeholder="Weekly flyer / retailer app / in-store" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={set("description")} rows={2} /></div>
          <div><Label>Terms</Label><Textarea value={form.terms} onChange={set("terms")} rows={2} placeholder="Validity, restrictions…" /></div>
        </div>
        <DialogFooter>
          <Button data-testid="submit-deal-confirm-btn" onClick={submit} disabled={busy || !form.title || !form.retailer || !form.deal_url} className="rounded-full">
            {isEdit ? <><Pencil className="mr-1 h-4 w-4" /> Save changes</> : <><Send className="mr-1 h-4 w-4" /> Submit for review</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
